/**
 * Device push-token registration (native only). Pure-ish helpers that take the
 * authenticated apiClient — no React. The hooks in ../hooks orchestrate them.
 */
import { Platform } from 'react-native'
import * as Notifications from 'expo-notifications'
import Constants from 'expo-constants'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { logger } from '../../helpers/logger.helper'
import { API_URL, API_ENDPOINTS } from '../../../constants/api'
import { registerPushToken } from './notificationsApi'
import type { ApiClient } from './notificationsApi'
import type { PushPlatform } from '../types/notifications.types'
import {
  PUSH_ANDROID_CHANNEL_ID,
  PUSH_ANDROID_CHANNEL_NAME,
  PUSH_LAST_SENT_STORAGE_KEY,
} from '../constants/pushRegistration.constants'

/** EAS projectId is required to mint an Expo push token. */
function getProjectId(): string | null {
  const fromExpoConfig = Constants.expoConfig?.extra?.eas?.projectId
  const fromEasConfig = (Constants as { easConfig?: { projectId?: string } }).easConfig?.projectId
  return (fromExpoConfig as string | undefined) ?? fromEasConfig ?? null
}

function currentPlatform(): PushPlatform | null {
  if (Platform.OS === 'ios') return 'ios'
  if (Platform.OS === 'android') return 'android'
  return null
}

function deviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  } catch {
    return ''
  }
}

async function ensureAndroidChannel(): Promise<void> {
  if (Platform.OS !== 'android') return
  await Notifications.setNotificationChannelAsync(PUSH_ANDROID_CHANNEL_ID, {
    name: PUSH_ANDROID_CHANNEL_NAME,
    importance: Notifications.AndroidImportance.DEFAULT,
  })
}

/**
 * Mint the Expo token and POST it to the server, skipping the network call when
 * the same token+timezone was already sent. Returns the token, or null on failure.
 */
async function syncToken(apiClient: ApiClient, platform: PushPlatform): Promise<string | null> {
  const projectId = getProjectId()
  if (!projectId) {
    logger.warn('[push] No EAS projectId — cannot mint push token')
    return null
  }

  await ensureAndroidChannel()
  const timezone = deviceTimezone()

  const { data: token } = await Notifications.getExpoPushTokenAsync({ projectId })
  if (!token) return null

  const fingerprint = `${token}|${timezone}`
  const lastSent = await AsyncStorage.getItem(PUSH_LAST_SENT_STORAGE_KEY)
  if (lastSent === fingerprint) return token

  await registerPushToken(apiClient, { token, platform, timezone })
  await AsyncStorage.setItem(PUSH_LAST_SENT_STORAGE_KEY, fingerprint)
  return token
}

/**
 * Register the device only if notification permission is ALREADY granted — never
 * prompts. Safe to call on every sign-in. No-op on web / unsupported platforms.
 */
export async function registerIfPermissionGranted(apiClient: ApiClient): Promise<void> {
  const platform = currentPlatform()
  if (!platform) return
  try {
    const { granted } = await Notifications.getPermissionsAsync()
    if (!granted) return
    await syncToken(apiClient, platform)
  } catch (error) {
    logger.warn('[push] registerIfPermissionGranted failed', {
      error: error instanceof Error ? error.message : String(error),
    })
  }
}

/**
 * Prompt for permission (OS dialog) and, if granted, register the device.
 * Call this behind a soft pre-prompt at a high-intent moment.
 * @returns true if permission ended up granted
 */
export async function requestPermissionAndRegister(apiClient: ApiClient): Promise<boolean> {
  const platform = currentPlatform()
  if (!platform) return false
  try {
    const existing = await Notifications.getPermissionsAsync()
    const granted = existing.granted
      ? true
      : (await Notifications.requestPermissionsAsync()).granted
    if (!granted) return false
    await syncToken(apiClient, platform)
    return true
  } catch (error) {
    logger.warn('[push] requestPermissionAndRegister failed', {
      error: error instanceof Error ? error.message : String(error),
    })
    return false
  }
}

/**
 * Remove this device's token from the server on sign-out, then clear local state.
 * Called from the auth context BEFORE the session is torn down, so it authenticates
 * with the still-valid access token (the hook-based apiClient is gone by sign-out).
 * Without this, a shared device keeps receiving the signed-out user's pushes.
 */
export async function unregisterDeviceTokenOnSignOut(accessToken: string | null | undefined): Promise<void> {
  const platform = currentPlatform()
  try {
    if (platform && accessToken) {
      const projectId = getProjectId()
      if (projectId) {
        const { data: token } = await Notifications.getExpoPushTokenAsync({ projectId })
        if (token) {
          await fetch(`${API_URL}${API_ENDPOINTS.users.pushToken}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ token }),
          })
        }
      }
    }
  } catch (error) {
    logger.warn('[push] unregisterDeviceTokenOnSignOut failed', {
      error: error instanceof Error ? error.message : String(error),
    })
  } finally {
    await AsyncStorage.removeItem(PUSH_LAST_SENT_STORAGE_KEY).catch(() => undefined)
  }
}
